/**
 * Tanjong Life · "Build the claims document completeness process by describing it."
 *
 * tl-pr11 is ALREADY on the shelf (Deployed v3 — it ran DOC-3305 and CDC-3305). The
 * conversation assembles the same process on the open canvas from an outline that mirrors
 * the seeded doc (tests/tanjong-scenarios.test.ts pins the mirror), asks the two questions
 * that make it a governed workflow, tests it, and ends by opening the deployed one rather
 * than deploying a second copy.
 *
 * Keywords are compounded on `completeness`, `missing document` and `document chase` —
 * spans no other story in this org owns — and carry no bare `claim`.
 */
import type { Scenario } from '../director.js';
import { MAKE_COMPLETENESS } from './tanjong-make-intents.js';
import { outlineDoc, slicer, tlTrigger } from './tanjong-canvas.js';

const CEA = { kind: 'agent', label: 'Claims Evidence Analyst' } as const;

const DOC = outlineDoc({
  pk: 'tl-pr11',
  name: 'Claims document completeness',
  agent: {
    name: 'Completeness Sentinel',
    role: 'Runs claims document completeness: every container read, the missing one named',
    handle: '@completeness-sentinel',
    icon: 'doc',
    accent: 'teal',
  },
  triggers: [
    tlTrigger(
      'trg-m365-mail-received',
      0,
      'A claim, or an answer to a request, on the intake mailbox or the fax gateway.',
    ),
    tlTrigger(
      'trg-claims-efiling-submitted',
      1,
      'A hospital or accredited clinic files the claim electronically.',
    ),
  ],
  objective: 'tl-o1',
  metrics: ['tl-m-completeness'],
  stages: [
    {
      name: 'Intake',
      color: 'teal',
      steps: [
        {
          name: 'Receive the claim on the channel it arrived on',
          type: 'application',
          app: 'm365',
          desc: 'Portal, mail or fax intake. The format is not the claimant’s problem to solve, and no channel is replaced.',
        },
        {
          name: 'Fetch every attachment and fax page',
          type: 'application',
          app: 'm365',
          desc: 'Phone photographs, Group 4 fax pages, desk scans and born-digital PDFs are all containers. Each one is fetched as it arrived; none is converted before it has been read.',
        },
        {
          name: 'Read every container, and score each reading',
          type: 'application',
          app: 'modelgateway',
          desc: 'Each container is read on its own and its reading carries a confidence for that container, never a blend. A fax reads at seventy-one, a phone photograph at seventy-nine, a PDF at ninety-nine — and anything below ninety is flagged for a person. Nothing is kept beyond the call.',
        },
        {
          name: 'Identify the policy and the benefit claimed',
          type: 'application',
          app: 'policyledger',
          desc: 'Which benefit is being claimed decides which documents the file needs. Getting this wrong is how a desk asks for the right document on the wrong claim.',
        },
        {
          name: 'Open the claim record',
          type: 'application',
          app: 'claimscore',
          desc: 'The claim record is opened in claims administration with every container attached and every reading beside it. This is the only write in the intake stage, and it creates a record rather than deciding anything about it.',
        },
      ],
    },
    {
      name: 'Completeness',
      color: 'blue',
      steps: [
        {
          name: 'Stamp the requirement-set version',
          type: 'script',
          desc: 'Which requirements this file was checked against, and when they became effective. Benefit schedules change; a file judged incomplete under last year’s rules should say so.',
        },
        {
          name: 'Read the requirement set for this benefit',
          type: 'agent',
          performer: CEA,
          desc: 'Five documents for a hospitalisation claim, and the reason the benefit needs each. The set is read from the governed knowledge base at the version effective on the event date — not from the underwriting rules engine, which holds no claims requirements.',
        },
        {
          name: 'Check every document against the requirements',
          type: 'agent',
          performer: CEA,
          desc: 'Invokes the Claims Evidence Analyst. It compares what arrived to what the benefit needs — and it reads the documents rather than counting attachments.',
        },
        {
          name: 'Name the missing document exactly',
          type: 'agent',
          performer: CEA,
          desc: 'Not "the outstanding paperwork". The pre-authorisation form, by name, with the reason this benefit needs it. Four of the seven days on the old run went on asking for one document by the wrong name.',
        },
        {
          name: 'Assert the claim was not adjudicated',
          type: 'script',
          desc: 'A rule in code. Nothing upstream of the assessor decides anything — this workflow validates completeness, and the number it publishes measures whether a file arrived complete rather than whether a decision was taken without a person.',
        },
      ],
    },
    {
      name: 'Governed request',
      color: 'amber',
      milestone: {
        name: 'File complete',
        desc: 'Everything the benefit requires is on the file, at the first ask.',
        guard: 'file_complete',
      },
      steps: [
        {
          name: 'Draft the request naming the document and the reason',
          type: 'agent',
          performer: CEA,
          guard: 'missing_named',
          desc: 'One request, naming everything outstanding at once. The old run sent two letters because the first did not say what it wanted.',
        },
        {
          name: 'Send the request on the channel the claim arrived on',
          type: 'application',
          app: 'm365',
          guard: 'request_drafted',
          desc: 'Whatever they used to reach the desk. A claimant who filed by fax should not have to learn a portal to answer a question.',
        },
        {
          name: 'Record every data touch against its lawful basis',
          type: 'script',
          desc: 'PDPA, as a step rather than a banner. Every read of a claimant’s record is written down with the basis it was made under, on every request.',
        },
      ],
    },
    {
      name: 'Adjudication',
      color: 'pink',
      steps: [
        {
          name: 'Assemble the complete file for the assessor',
          type: 'script',
          guard: 'file_complete',
          desc: 'Everything in one place, indexed against the requirement each document satisfies. The assessor still spends thirty-one minutes on the part that matters — which was never the slow part.',
        },
        {
          name: 'Adjudicate the claim',
          type: 'human',
          performer: { kind: 'group', label: 'Claims Managers' },
          sla: { amount: '2', unit: 'd' },
          desc: 'The only gate, and the only place a claim is decided. The assessor accepts, declines or queries — on a complete file, at the first ask.',
        },
      ],
    },
    {
      name: 'Settle & record',
      color: 'green',
      milestone: {
        name: 'Decided and recorded',
        desc: 'An assessor decided, and the rules version is on the record.',
        guard: 'adjudication_recorded',
      },
      steps: [
        {
          name: 'Write the decision record',
          type: 'application',
          app: 'claimscore',
          guard: 'adjudicated',
          desc: 'Downstream of the assessor and guarded on their decision. The workflow writes the decision record after the approval and never before it; an agent identity attempting this write is refused. Nothing here writes to policy administration.',
        },
        {
          name: 'Track first-pass completeness',
          type: 'agent',
          performer: { kind: 'agent', label: 'Completeness Sentinel' },
          desc: 'How often a file arrives complete on the first ask. It is the number this workflow is actually accountable for, and it is deliberately not a rate of decisions taken.',
        },
      ],
    },
  ],
});

const S = slicer(DOC);
const P_NAME = S.slice({ name: DOC.name, agent: DOC.agent });
const P_TRIGGERS = S.slice({ triggers: DOC.triggers });
const P1 = S.stagePatch(0);
const P2 = S.stagePatch(1);
const P3 = S.stagePatch(2);
const P4 = S.stagePatch(3);
const P5 = { ...S.stagePatch(4), objective: DOC.objective ?? null, metrics: DOC.metrics ?? [] };

export const tanjongBuildCompleteness: Scenario = {
  id: 'tanjong-build-completeness',
  orgId: 'tanjong-life',
  keywords: [
    'document completeness',
    'claims completeness',
    'completeness check',
    'missing document',
    'document chase',
    'incomplete file',
  ],
  intents: [MAKE_COMPLETENESS],
  steps: [
    {
      do: 'say',
      text: 'The slow part of this one is not assessment. It is asking for one document by the wrong name — so that is what I will build around.',
      delay: 550,
    },
    {
      do: 'canvas',
      patch: P_NAME,
      summary: 'Named it Claims document completeness · Completeness Sentinel set as the driver',
      delay: 700,
    },
    {
      do: 'canvas',
      patch: P_TRIGGERS,
      summary: 'Added 2 triggers — claims intake mailbox · hospital e-filing',
      delay: 600,
    },
    {
      do: 'canvas',
      patch: P1,
      summary: 'Stage 1 · Intake — 5 activities, every container read with its own confidence',
      delay: 800,
    },
    {
      do: 'say',
      text: 'Both triggers land in the same stage, so a claimant answering a request re-enters the process rather than starting a new one. That alone removes a second case number from the desk. And the third box reads each container on its own — a fax at seventy-one, a phone photograph at seventy-nine, a PDF at ninety-nine — and flags anything under ninety for a person.',
      delay: 900,
    },
    { do: 'canvas', patch: P2, summary: 'Stage 2 · Completeness — 5 activities', delay: 850 },
    {
      do: 'ask',
      id: 'naming',
      prompt: 'When something is missing, what should the request say?',
      options: [
        { id: 'exact', label: 'Name the document and why the benefit needs it (recommended)' },
        { id: 'generic', label: 'Ask for the outstanding paperwork' },
        { id: 'list', label: 'Send the full requirements list' },
      ],
      delay: 700,
    },
    {
      do: 'branch',
      cases: {
        generic: [
          {
            do: 'say',
            text: 'That is the run you have today, and it costs four of the seven days: the claimant returns the wrong document, a second letter names the form, and they had it all along. None of that is anybody being slow. I will name it exactly.',
            delay: 950,
          },
        ],
        list: [
          {
            do: 'say',
            text: 'The full list makes the claimant do the diff. I will name only what is outstanding, with the reason — you can attach the full list underneath.',
            delay: 800,
          },
        ],
      },
      else: [
        {
          do: 'say',
          text: 'Named, with the reason. The Doctor’s Statement above SGD 2,000, not “the outstanding paperwork”.',
          delay: 600,
        },
      ],
    },
    {
      do: 'insight',
      title: 'And one box that is there to stop something',
      body: 'The last box in that stage asserts the claim was not adjudicated. This workflow validates completeness; the number it publishes measures whether a file ARRIVED COMPLETE — a different question from whether a decision was taken without a person, and this act publishes no number for the second.',
      sources: ['Completeness metric definition', 'Claims requirement sets · Singapore'],
      delay: 1200,
    },
    {
      do: 'canvas',
      patch: P3,
      summary: 'Stage 3 · Governed request — 3 activities · milestone M1',
      delay: 850,
    },
    {
      do: 'say',
      text: 'The last box in that stage records every data touch against its lawful basis. PDPA as a step that runs on every request, rather than a line on a slide.',
      delay: 800,
    },
    {
      do: 'askText',
      id: 'assessor',
      prompt: 'Who adjudicates a claim?',
      placeholder: 'e.g. the Claims Managers',
      delay: 650,
    },
    {
      do: 'say',
      text: 'That group owns the only gate on this canvas. The assessor still spends thirty-one minutes on the part that matters — which was never the slow part. What changed is that they start on a complete file.',
      delay: 850,
    },
    { do: 'canvas', patch: P4, summary: 'Stage 4 · Adjudication — the single human gate', delay: 800 },
    {
      do: 'canvas',
      patch: P5,
      summary: 'Stage 5 · Settle & record — decision record, first-pass completeness · milestone M2',
      delay: 800,
    },
    {
      do: 'test',
      title: 'Testing components…',
      items: [
        { label: 'Multi-channel intake', sub: 'E-filing, mail and fax all resolve to one claim' },
        { label: 'Container reading', sub: 'Confidence per container · anything under 90 flagged' },
        { label: 'Benefit identification', sub: 'Decides which documents the file needs · read only' },
        { label: 'Requirement set read', sub: 'Governed base, at the version in force' },
        { label: 'Document matching', sub: 'Each arrival mapped to the requirement it satisfies' },
        { label: 'Exact naming', sub: 'The missing document named as the claimant would know it' },
        { label: 'Adjudication assertion', sub: 'Nothing upstream of the assessor decides' },
        { label: 'Lawful-basis record', sub: 'Every data touch, on every request · PDPA' },
        { label: 'Re-entry path', sub: 'A returned document rejoins the same claim' },
        { label: 'Decision-write guard', sub: 'No write to claims administration without the assessor' },
      ],
      result: 'All components passed',
      delay: 600,
    },
    {
      do: 'say',
      text: 'All passed. Seventeen activities, one gate, and a script whose only job is to make sure nothing before that gate decided anything.',
      delay: 6600,
    },
    {
      do: 'say',
      text: 'This one is already on the shelf as v3 — what you just watched assemble is the process that ran DOC-3305 and CDC-3305 today. Open it and the two runs are there, with the trigger that started each and the trace behind every read.',
      delay: 900,
    },
    {
      do: 'link',
      label: 'Open Claims document completeness — already deployed, v3',
      href: '/processes/tl-pr11',
      delay: 600,
    },
  ],
};
